import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Surface } from './Surface';
import { cn } from '../../lib/utils';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useResizablePanel } from '../../hooks/useResizablePanel';

/**
 * ArtifactSurface — the right-hand pane holding whatever is being worked on:
 * a document, a mockup preview, a task changeset, a file.
 *
 * Composes Surface + motion + a resize handle so Surface itself stays a dumb
 * skin. Everything about how a panel enters, leaves and resizes lives here, so
 * all four callers behave identically.
 *
 * ── Width is never animated ──────────────────────────────────────────────────
 * Monaco's automaticLayout installs a ResizeObserver and Sandpack re-lays-out
 * its whole iframe document, so animating the container width makes them
 * re-measure on every frame of the transition. Width therefore snaps in a
 * single layout pass and only transform + opacity animate, which the compositor
 * handles without ever touching layout.
 *
 * A tween rather than a spring: the flex sibling next door resizes instantly,
 * so a springy overshoot on x reads as a wobble against a static edge.
 */
export function ArtifactSurface({
    label,
    // Rendered above the panel body, outside its scroll container — the panel
    // switcher goes here. A sibling of the body rather than a child of it, so
    // the panels can keep sizing themselves with `h-full`: they were written
    // when they owned the whole surface, and every one of them would have to
    // change if this took height away from inside their container.
    toolbar,
    // { min, max, initial, storageKey } — omit to make the panel fixed-width.
    resize,
    // Take the whole surface row. The caller is responsible for hiding its
    // other surfaces; this side just stops being a fixed-width column.
    // Resizing is suspended (not reset), so collapsing restores the old width.
    expanded = false,
    className,
    children,
    ...rest
}) {
    const isMobile = useIsMobile();
    const reduceMotion = useReducedMotion();
    const { width, isResizing, handleProps } = useResizablePanel({
        min: resize?.min ?? 320,
        max: resize?.max ?? 900,
        initial: resize?.initial ?? 480,
        storageKey: resize?.storageKey,
        enabled: !!resize && !isMobile && !expanded,
    });

    return (
        <Surface
            as={motion.aside}
            variant="panel"
            aria-label={label}
            width={width}
            className={cn(expanded ? 'flex-1' : 'flex-shrink-0', className)}
            initial={reduceMotion ? false : isMobile ? { y: '100%' } : { opacity: 0, x: 16 }}
            animate={isMobile ? { y: 0 } : { opacity: 1, x: 0 }}
            exit={reduceMotion ? { opacity: 0 } : isMobile ? { y: '100%' } : { opacity: 0, x: 16 }}
            transition={
                reduceMotion
                    ? { duration: 0 }
                    : isMobile
                        ? { type: 'spring', stiffness: 260, damping: 28 }
                        : { duration: 0.18, ease: [0.32, 0.72, 0, 1] }
            }
            {...rest}
        >
            {!isMobile && resize && !expanded && (
                // Sits just inside the panel's left edge, reaching into the gap
                // between surfaces. Invisible until you approach it.
                <div
                    {...handleProps}
                    className="group absolute left-0 top-0 bottom-0 z-20 w-2 cursor-ew-resize outline-none"
                    title="Drag to resize · double-click to reset"
                >
                    <div className="absolute left-0.5 top-1/2 h-16 w-1 -translate-y-1/2 rounded-full bg-white/10 transition-colors group-hover:bg-primary/50 group-focus-visible:bg-primary/70" />
                </div>
            )}

            {toolbar}

            {/* While dragging, the body must not eat pointer events — otherwise
                an iframe or Monaco swallows the move and the drag stalls. */}
            <div className={cn('flex min-h-0 flex-1 flex-col', isResizing && 'pointer-events-none')}>
                {children}
            </div>
        </Surface>
    );
}

export default ArtifactSurface;
